import type { Organization } from '@rtkwlf/eloc';
import type { LoaderFunction } from 'react-router-dom';
import { redirect } from 'react-router-dom';

import { getUser, verifyUser } from '@platform/core/auth';
import { ForbiddenError, InternalServerError } from '@platform/core/errors';
import { queryClient } from '@platform/core/state/query-client';

import { organizationsQuery } from '@apis/eloc/queries/organizations';

export const organizationsLoader: LoaderFunction = async ({ request }) => {
  // wait for user to be authenticated before moving further.
  await verifyUser();

  let organizations: Organization[];

  try {
    organizations = await queryClient.fetchQuery(organizationsQuery());
  } catch (error: unknown) {
    const fetchError = error as any; // instanceof ResponseError does not work here
    const status = fetchError?.response?.status;

    if (status >= 400 && status < 500) {
      const user = await getUser();

      throw new ForbiddenError({
        user,
        reason: 'ACCESS_DENIED',
        showAction: true,
      });
    }

    throw new InternalServerError();
  }

  if (!organizations.length) {
    const user = await getUser();

    throw new ForbiddenError({
      user,
      reason: 'NO_ORGANIZATIONS_FOUND',
      showAction: true,
    });
  }

  if (organizations.length === 1) {
    const url = new URL(request.url);

    // The request url will contain a 'from' search param if user has been redirected to the root to set an org id.
    // Redirect user to saved path once the org id has been set.
    const from = url.searchParams.get('from');

    return redirect(`/${organizations[0].id}${from ?? ''}`);
  }

  return null;
};
